import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Col, Row } from 'react-flexbox-grid';
import { Reducers } from 'store/types';
import { logout, updateRefreshToken } from 'store/authentication/action';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import { UserDto } from 'api/users/models/UserDto';
import { RoleDto } from 'api/roles/models/RoleDto';
import UsersService from 'api/users/UsersService';
import RolesService from 'api/roles/RolesService';
import { CheckAvailabilityDto } from 'api/common/models/CheckAvailabilityDto';
import CustomFile from 'common/models/CustomFile';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import CheckBoxController from 'common/components/checkBox/CheckBoxController';
import MediaSelector from 'common/components/mediaSelector/MediaSelector';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import Button from 'common/components/button/Button';
import styles from './DriverUserTab.module.scss';
import InputError from 'common/components/inputError/InputError';
import { Types } from 'screens/vehicle/VehicleScreen';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import SelectController from 'common/components/select/SelectController';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import CountriesService from '../../../../api/countries/CountriesService';
import LanguagesService from 'api/languages/LanguagesService';
import CompaniesService from 'api/companies/CompaniesService';
import { CompanyDto } from 'api/companies/models/CompanyDto';
import IdentificationTagsService from 'api/identificationTags/IdentificationTagsService';
import ReactTooltip from 'react-tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import Select from 'common/components/select/Select';
import VehiclesService from 'api/vehicles/VehiclesService';
import PhoneNumberInputController from 'common/components/phoneNumberInput/PhoneNumberInputController';

type Props = {
};

const DriverUserTab: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const dispatch = useDispatch();
    const [countries, setCountries] = useState<SelectValueLabel[]>([]);
    const { id, driverId, type } = useParams<{ id: string, driverId: string, type: Types }>();
    const [allRoles, setAllRoles] = useState<RoleDto[]>([]);
    const [user, setUser] = useState<UserDto>();
    const form = useForm<UserDto>({ shouldUnregister: false, defaultValues: { ...user, vehicleUserAssociation: user?.vehicleUserAssociation ?? 'UNLIMITED' } });
    const { register, handleSubmit, errors, watch } = form;
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasUsersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_USERS_WRITE']);
    const hasRolesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ROLES_WRITE']);
    const hasRolesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ROLES_READ']);
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const [mediaFile, setMediaFile] = useState<CustomFile | null>(null);
    const [removeMedia, setRemoveMedia] = useState(false);
    const [username, setUsername] = useState('');
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<UserDto | null>(null);
    const [languages, setLanguages] = useState<SelectValueLabel[]>([]);
    const [company, setCompany] = useState<CompanyDto>();
    const [tags, setTags] = useState<SelectValueLabel[]>([]);
    const [assetsOptions, setAssetsOptions] = useState<SelectValueLabel[]>([]);
    const [defaultDriverAssets, setDefaultDriverAssets] = useState<SelectValueLabel[]>([]);
    const [reserveOnlyRoleIds, setReserveOnlyRoleIds] = useState<string[]>([]);
    const prevHasReserveRoleRef = React.useRef<boolean>(false);

    const getData = async () => {
        Loading.show();

        try {

            const [
                languagesData,
                companyDate
            ] = await Promise.all([
                LanguagesService.GetAllForSelectItem(),
                CompaniesService.getCurrent()
            ]);
            setLanguages(languagesData);


            setCompany(companyDate);

            const allDBRoles = await RolesService.getAllForCompany();
            let userObj: UserDto = { id: '', realName: '', email: '', roles: [], temporaryRoles: [], country: companyDate.country, languageId: company?.languageId, identificationTagId: '' };
            if (driverId) {
                userObj = await UsersService.getById(driverId);
            }

            const allDBAssets = await VehiclesService.catalog({ fromUsersPage: true }); 

            // Veiculos em que o driver está associado no ativo, são obrigatórios, sempre!
            const userId = driverId || id;
            let defaultDriverVehicles: SelectValueLabel[] = [];
            if (userId) {
                defaultDriverVehicles = await VehiclesService.catalogByDefaultDriver(userId) || [];
                setDefaultDriverAssets(defaultDriverVehicles);
            }

            if (allDBAssets) {
                const defaultDriverIds = defaultDriverVehicles?.map(v => v.value) || []; //tirar os veiculos que vieram com o user no ativo das opcoes de escolha
                setAssetsOptions(allDBAssets.filter(a => !defaultDriverIds.includes(a.value))); 
            }

            if (id && !driverId) {
                userObj = await UsersService.getById(id);
            }
            const reserveRoleIds = userObj.reserveOnlyRoleIds || await UsersService.getReserveOnlyRoleIds();
            setReserveOnlyRoleIds(reserveRoleIds);

            setAllRoles([...allDBRoles]);
            userObj.temporaryRoles = allDBRoles.map(role => {
                const __role = { ...role };
                __role.checked = (userObj.roles?.includes(__role.name) ?? false);
                return __role;
            });

            userObj.temporaryRoles.forEach(role => {
                if (role.readOnly || role.system) {
                    role.name = t(('common.roles.' + role.name) as any);
                }
            });

            const coutriesData = await CountriesService.catalog();
            setCountries(coutriesData.map(x => {
                return { value: x.name, label: (t('common.countries.' + x.name as any)) }
            }));

            const tagsData = await IdentificationTagsService.catalog();
            setTags(tagsData);

            const defaultDriverIds = defaultDriverVehicles?.map(v => v.value) || [];
            if (userObj.assetIds) {
                userObj.assetIds = userObj.assetIds.filter(id => !defaultDriverIds.includes(id));
            }

            setUser(userObj);
            setUsername(userObj.realName);
            setIsDetails(type === 'details');

            form.reset(userObj);

            prevHasReserveRoleRef.current = allDBRoles.some(role =>
                (userObj.roles?.includes(role.name) ?? false) && reserveRoleIds.includes(role.id)
            );

            Loading.hide();
        }
        catch (error) {
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get catalog users types`,
                error
            );
            Loading.hide();
        }
    }

    const onCancel = () => {
        // if (driverId) {
        //     history.push('/drivers');
        //     return;
        // }
        history.goBack();
    };

    useEffect(() => {
        getData();
    }, [type]);

    const watchedRolesForReserve = watch<string, RoleDto[]>('temporaryRoles');
    const isDirty = form.formState.isDirty;
    useEffect(() => {
        if (!isDirty || isDetails) return;
        if (!watchedRolesForReserve || !allRoles.length || !reserveOnlyRoleIds.length) return;

        const hasReserveRole = watchedRolesForReserve.some((r, i) => r.checked && reserveOnlyRoleIds.includes(allRoles[i]?.id));
        const prevHasReserveRole = prevHasReserveRoleRef.current;
        prevHasReserveRoleRef.current = hasReserveRole;

        if (hasReserveRole && !prevHasReserveRole) {
            form.setValue('vehicleUserAssociation', 'RESERVE_ONLY');
            form.setValue('assetIds', []);
        } else if (!hasReserveRole && prevHasReserveRole && form.getValues('vehicleUserAssociation') === 'RESERVE_ONLY') {
            form.setValue('vehicleUserAssociation', 'UNLIMITED');
            form.setValue('assetIds', []);
        }
    }, [JSON.stringify(watchedRolesForReserve?.map(r => r.checked)), isDirty]);

    const onAddAvatar = (files: CustomFile[]) => {
        if (files.length > 0) {
            setMediaFile(files[0]);
        }
    }

    const onRemoveAvatar = () => {
        setRemoveMedia(true);
        if (user) {
            setUser({
                ...user,
                media: null,
            });
        }
    }

    const onSubmit = async (formData: UserDto) => {
        try {

            if (!formData.temporaryRoles || formData.temporaryRoles?.find(e => e.checked === true) == null) {
                addToast(t('common.messages.complete_required_fields'), { appearance: 'warning' });
                return;
            }

            if (formData.internalId && formData.internalId.trim()) {
                const internalIdData: CheckAvailabilityDto = { table: 'asp_net_users', column: 'internal_id', value: formData.internalId.trim(), companyId: true, itemEdit: user?.id };
                const checkAvailabilityInternalId = await UsersService.checkAvailability(internalIdData);
                if (!checkAvailabilityInternalId) {
                    addToast(t('common.messages.internal_id_already_exists' as any), { appearance: 'warning' });
                    return;
                }
            }

            const emailData: CheckAvailabilityDto = { table: 'asp_net_users', column: 'normalized_email', value: formData.email.toLocaleUpperCase(), companyId: true, itemEdit: user?.id };
            const checkAvailabilityEmail = await UsersService.checkAvailability(emailData);
            if (!checkAvailabilityEmail) {
                addToast(t('common.messages.email_already_exists'), { appearance: 'warning' });
                return;
            }

            emailData.companyId = false;
            const checkAvailabilityEmailAllEntities = await UsersService.checkAvailability(emailData);
            if (!checkAvailabilityEmailAllEntities) {
                addToast(t('common.messages.email_already_exists_all_entities'), { appearance: 'warning' });
                return;
            }

            Loading.show();
            if (user) {
                user.realName = formData.realName;
                user.internalId = formData.internalId;
                user.email = formData.email;
                user.phoneNumber = formData.phoneNumber;
                user.address = formData.address;
                user.zipcode = formData.zipcode;
                user.country = formData.country;
                user.languageId = formData.languageId;
                user.roles = [];
                user.removeMedia = removeMedia;
                user.identificationTagId = formData.identificationTagId;
                user.vehicleUserAssociation = formData.vehicleUserAssociation;
                const defaultDriverAssetIds = defaultDriverAssets.map(a => a.value);
                user.assetIds = [...defaultDriverAssetIds, ...(formData.assetIds || [])];
                if (formData.temporaryRoles) {
                    formData.temporaryRoles.forEach((r, i) => {
                        if (r.checked) {
                            user.roles?.push(allRoles[i].name);
                        }
                    });
                }
            }

            let response: { emailChanged: boolean } | undefined;
            if (user && user.id != '') {
                response = await UsersService.update(user, mediaFile);
            } else if (user) {
                await UsersService.create(user, mediaFile);
            }

            if (response?.emailChanged && user?.id === loggedUser?.id) {
                Loading.hide();
                addToast(t('account.email_changed_relogin') as string, { appearance: 'warning' });
                setTimeout(() => dispatch(logout() as any), 2000);
                return;
            }

            if (user?.id === loggedUser?.id) {
                dispatch(updateRefreshToken());
            }

            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });
            onCancel();
        } catch (error) {
            if (error?.response?.status === 402) {
                addToast(t('common.errors.pack_limit_exceeded'), { appearance: 'warning' });
            }
            else { 
                addToast(t('common.messages.record_save_error'), {
                    appearance: 'error',
                });
            }
            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update user`,
                error
            );
            Loading.hide();
        }
    };

    const onError = async () => {
        addToast(t('common.errors.required_fields_empty'), { appearance: 'warning' });
    }

    const showRemoveItemDialog = async (item: UserDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await UsersService.remove(itemToRemove);
            onCancelRemove();
            onCancel();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete user`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.avatarContainer}>
                <div className={styles.avatarChooser}>
                    <MediaSelector
                        isProfilePic={true}
                        isDetails={isDetails}
                        isMulti={false}
                        onAddMedias={onAddAvatar}
                        onRemoveMedia={onRemoveAvatar}
                        initialMedias={user?.media ? [user?.media] : []}
                    />
                </div>
                <span className={styles.usernameCol}>{username}</span>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit, onError)}>
                <Row>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('users.name')} {!isDetails && '*'}
                            </Label>
                            <Input
                                name='realName'
                                placeholder={t('users.name')}
                                defaultValue={user?.realName}
                                disabled={isDetails}
                                ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                onChange={e => setUsername(e.target.value as any)}
                            />
                            <InputError error={errors.realName} />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('users.internal_id')}
                            </Label>
                            <Input
                                name='internalId'
                                placeholder={t('users.internal_id')}
                                defaultValue={user?.internalId}
                                disabled={isDetails}
                                ref={register({ required: false })}
                            />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('users.email')} {!isDetails && '*'}
                            </Label>
                            <Input
                                name='email'
                                placeholder={t('users.email')}
                                defaultValue={user?.email}
                                disabled={isDetails}
                                ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                            />
                            <InputError error={errors.email} />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('users.phone_number')}
                            </Label>
                            <PhoneNumberInputController
                                form={form}
                                name='phoneNumber'
                                placeholder={t('users.phone_number')}
                                disabled={isDetails}
                                defaultCountry={user?.country || company?.country}
                            />
                            <InputError error={errors.phoneNumber} />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('users.address')}
                            </Label>
                            <Input
                                name='address'
                                placeholder={t('users.address')}
                                defaultValue={user?.address}
                                disabled={isDetails}
                                ref={register({ required: false })}
                            />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('users.zipcode')}
                            </Label>
                            <Input
                                name='zipcode'
                                placeholder={t('users.zipcode')}
                                defaultValue={user?.zipcode}
                                disabled={isDetails}
                                ref={register({ required: false })}
                            />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('common.country')}
                            </Label>
                            <SelectController
                                form={form}
                                isClearable={true}
                                name='country'
                                options={countries}
                                placeholder={t('users.country')}
                                rules={{ required: false }}
                                isDisabled={isDetails}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                            />
                            <InputError error={errors.country} />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>{t('users.language')} {!isDetails && '*'}</Label>
                            <SelectController
                                form={form}
                                name='languageId'
                                options={languages ? languages.map(x => { return { ...x, label: x.fullLabel ?? '' } }) : []}
                                placeholder={t('users.language')}
                                rules={{ required: true }}
                                isDisabled={isDetails}
                            />
                            <InputError error={errors.languageId} />
                        </FormItem>
                    </Col>
                    <Col xs={12} md={6} lg={3}>
                        <FormItem>
                            <Label className={styles.label}>{t('users.identification_tags')}</Label>
                            <SelectController
                                form={form}
                                name='identificationTagId'
                                options={tags}
                                placeholder={t('users.identification_tags')}
                                rules={{ required: false }}
                                isDisabled={isDetails}
                                isClearable={true}
                            />
                            <InputError error={errors.identificationTagId} />
                        </FormItem>
                    </Col>


                    <Col xs={12}>
                        {(hasRolesReadPolicy || hasRolesWritePolicy) && <FormItem>
                            <Label className={styles.label}>
                                {t('roles.title')} {!isDetails && '*'}
                            </Label>
                            <div style={{ marginLeft: '20px' }}>

                                {(() => {
                                    const watchedRoles = watch<string, RoleDto[]>('temporaryRoles') || [];
                                    const checkedProtectedCount = watchedRoles.filter((r, idx) => r.checked && user?.protectedRoleIds?.includes(allRoles[idx]?.id)).length;
                                    return (<>
                                <Label className={styles.label}>
                                    {t('roles.system_roles')}
                                </Label>
                                <div className={styles.roles}>
                                    {user?.temporaryRoles.map((role, i) => {
                                        if (role.readOnly == false) return;
                                        const isProtectedRole = user?.protectedRoleIds?.includes(allRoles[i]?.id);
                                        const isLockedAdmin = isProtectedRole && watchedRoles[i]?.checked && user?.isLastAdmin && checkedProtectedCount <= 1;
                                        const lastAdminTooltip: string | undefined = isLockedAdmin ? t('users.last_admin_tooltip' as never) as string : undefined;
                                        return (<span key={i}>
                                            <span data-tip={lastAdminTooltip || undefined} data-for={isLockedAdmin ? `lastAdminToolTip_${i}` : undefined}>
                                                <CheckBoxController form={form as any}
                                                    name={`temporaryRoles[${i}].checked`}
                                                    label={role.name}
                                                    disabled={isDetails || !hasRolesWritePolicy || isLockedAdmin} />
                                            </span>
                                            {
                                                role.description.find(x => x.languageId == userProfile?.languageId)
                                                ? <FaInfoCircle data-tip={role.description.find(x => x.languageId == userProfile?.languageId)?.description} data-for={`companyRoleToolTip_${i}`} className={styles.tooltipIcon}/>
                                                : <></>
                                            }
                                            {isLockedAdmin && <ReactTooltip id={`lastAdminToolTip_${i}`} place="top" type="dark" effect="solid" className={styles.customTooltip}/>}
                                            <ReactTooltip id={`companyRoleToolTip_${i}`} place="top" type="dark" effect="solid" className={styles.customTooltip}/>
                                        </span>);
                                    })}

                                </div>

                                <Label className={styles.label} style={{ marginTop: 10 }}>
                                    {t('roles.company_roles')}
                                    <FaInfoCircle data-tip={t('roles.company_roles_tooltip')} data-for={'companyRolesToolTip'} className={styles.tooltipIcon} />
                                    <ReactTooltip id={'companyRolesToolTip'} place="top" type="dark" effect="solid" className={styles.customTooltip} />
                                </Label>
                                <div className={styles.roles}>
                                    {user?.temporaryRoles.map((role, i) => {
                                        if (role.readOnly == true) return;
                                        const isProtectedRole = user?.protectedRoleIds?.includes(allRoles[i]?.id);
                                        const isLockedAdmin = isProtectedRole && watchedRoles[i]?.checked && user?.isLastAdmin && checkedProtectedCount <= 1;
                                        const lastAdminTooltip: string | undefined = isLockedAdmin ? t('users.last_admin_tooltip' as never) as string : undefined;
                                        return (<span key={i}>
                                            <span data-tip={lastAdminTooltip || undefined} data-for={isLockedAdmin ? `lastAdminToolTip_${i}` : undefined}>
                                                <CheckBoxController form={form as any}
                                                    name={`temporaryRoles[${i}].checked`}
                                                    label={role.name}
                                                    disabled={isDetails || !hasRolesWritePolicy || isLockedAdmin} />
                                            </span>
                                            {isLockedAdmin && <ReactTooltip id={`lastAdminToolTip_${i}`} place="top" type="dark" effect="solid" className={styles.customTooltip}/>}
                                            {role.description.find(x => x.languageId == userProfile?.languageId) ? <FaInfoCircle data-tip={role.description.find(x => x.languageId == userProfile?.languageId)?.description} data-for={`companyRoleToolTip_${i}`} className={styles.tooltipIcon}/> : <></>}
                                            <ReactTooltip id={`companyRoleToolTip_${i}`} place="top" type="dark" effect="solid" className={styles.customTooltip} />
                                        </span>
                                        );
                                    })}

                                </div>
                                    </>);
                                })()}
                            </div>
                            <InputError error={form.formState.isSubmitted && watch<string, RoleDto[]>('temporaryRoles').filter(x => x.checked).length === 0 ? { type: 'dateRange', message: t('common.errors.error_no_role_selected') } : undefined} />
                        </FormItem>}
                    </Col>

                    <Col xs={12}>
                        {(hasRolesReadPolicy || hasRolesWritePolicy) && <FormItem>
                            <Label className={styles.label}>
                                {t('roles.assets_management')}
                                {/*<FaInfoCircle data-tip={t('roles.company_roles_tooltip')} data-for={'companyRolesToolTip'} className={styles.tooltipIcon} />
                                    <ReactTooltip id={'companyRolesToolTip'} place="top" type="dark" effect="solid" />*/}
                            </Label>
                            <div className={styles.assets}>
                                <Controller
                                    control={form.control}
                                    name="vehicleUserAssociation"
                                    defaultValue="UNLIMITED"
                                    render={({ value, onChange }) => (
                                        <div className={styles.assets}>
                                            <div className={styles.assetRadioGroup}>
                                                <label className={styles.assetRadioLabel}>
                                                    <input
                                                        type="radio"
                                                        value="UNLIMITED"
                                                        checked={value === 'UNLIMITED'}
                                                        disabled={isDetails}
                                                        onChange={() => {
                                                            onChange('UNLIMITED');
                                                            form.setValue('assetIds', []);
                                                        }}
                                                    />
                                                    {t('roles.assets_unlimited')}
                                                </label>

                                                <label className={styles.assetRadioLabel}>
                                                    <input
                                                        type="radio"
                                                        value="LIMITED"
                                                        checked={value === 'LIMITED'}
                                                        disabled={isDetails}
                                                        onChange={() => onChange('LIMITED')}
                                                    />
                                                    {t('roles.assets_limited')}
                                                </label>

                                                <label className={styles.assetRadioLabel}>
                                                    <input
                                                        type="radio"
                                                        value="RESERVE_ONLY"
                                                        checked={value === 'RESERVE_ONLY'}
                                                        disabled={isDetails}
                                                        onChange={() => {
                                                            onChange('RESERVE_ONLY');
                                                            form.setValue('assetIds', []);
                                                        }}
                                                    />
                                                    {t('roles.assets_reserve_only')}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                />

                                {/* MULTI SELECT */}
                                {watch('vehicleUserAssociation') === 'LIMITED' && (
                                    <div style={{ marginTop: 12 }}>

                                        {defaultDriverAssets.length > 0 && (
                                            <FormItem>
                                                <Label className={styles.label}>
                                                    {t('roles.default_driver_assets')}
                                                    <FaInfoCircle
                                                        data-tip={t('roles.default_driver_assets_tooltip')}
                                                        data-for={'defaultDriverAssetsToolTip'}
                                                        className={styles.tooltipIcon}
                                                    />
                                                    <ReactTooltip id={'defaultDriverAssetsToolTip'} place="top" type="dark" effect="solid" />
                                                </Label>
                                                <Select
                                                    isMulti
                                                    isClearable={false}
                                                    isDisabled={true}
                                                    showMultiOptions={true}
                                                    options={defaultDriverAssets} 
                                                    value={defaultDriverAssets}
                                                    styles={{
                                                        multiValueRemove: (base:any) => ({
                                                            ...base,
                                                            display: 'none'
                                                        })
                                                    }}
                                                />
                                            </FormItem>
                                        )}

                                        <FormItem>
                                            <Label className={styles.label}>
                                                {t('roles.select_assets')}
                                            </Label>

                                            <Controller
                                                control={form.control}
                                                name="assetIds"
                                                render={({ onChange, value }) => (
                                                    <Select
                                                        isMulti
                                                        isClearable
                                                        isDisabled={isDetails}
                                                        showMultiOptions={true}
                                                        growScrollOnOpen
                                                        options={assetsOptions}
                                                        value={value ? assetsOptions.filter(x => value.includes(x.value)) : []}
                                                        onChange={(data: SelectValueLabel[]) =>
                                                            onChange(data?.map(x => x.value))
                                                        }
                                                    />
                                                )}
                                            />
                                        </FormItem>
                                    </div>
                                )}
                            </div>
                        </FormItem>}
                    </Col>
                </Row>

                <div className={styles.buttonContainer}>
                    <Button preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onCancel()} />
                    {isDetails && hasUsersWritePolicy && !user?.isLastAdmin &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            onClick={() => showRemoveItemDialog({ id: user?.id } as UserDto)} />
                    }
                    {isDetails && hasUsersWritePolicy &&
                        <Button
                            type='button'
                            text={t('common.edit')}
                            onClick={() => history.push(`/drivers/edit/${driverId ?? id}/user/list`)} />
                    }
                    {!isDetails && hasUsersWritePolicy &&
                        <Button
                            type='submit'
                            text={t('common.save')} />
                    }
                </div>
            </form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.realName ?? '' })} />
        </div>
    );
}

export default DriverUserTab;